import OktaAuth from '@okta/okta-auth-js';
import { ENDSESSION_ENDPOINT, OKTA_CLIENT_ID, OKTA_ISSUER } from './app.constants';

/**
 * Where the IdP returns the browser after the end-session redirect (see AuthService.logout).
 *
 * This path is NOT routed by Angular: the portal ingress (deployment.yml) 301s it to the Khoros
 * community front-channel logout, which is the last leg of the global logout chain. It must be
 * registered as a sign-out/post-logout redirect URI for this client in Okta, otherwise the IdP
 * rejects the end-session request.
 */
export const POST_LOGOUT_REDIRECT_URI = window.location.origin + '/post-logout-redirect';

/**
 * Central OktaAuth instance for the portal (Authorization Code + PKCE).
 *
 * Token storage: 'sessionStorage' keeps tokens for the tab session — they survive a page
 * reload (so no full-page redirect to Okta on every refresh) but are not persisted to disk
 * across browser restarts. Alternatives (single place to change):
 *   - 'memory'       : most secure, but tokens are lost on reload → a redirect round-trip each refresh.
 *   - 'localStorage' : cross-tab/persistent, but weaker XSS posture (this was part of the old problem).
 *
 * autoRenew silently refreshes the access token before it expires (using the refresh token from
 * the 'offline_access' scope), which removes the old "blank page after ~1 hour" failure.
 */
export const oktaAuth = new OktaAuth({
    issuer: OKTA_ISSUER,
    clientId: OKTA_CLIENT_ID,
    redirectUri: window.location.origin + '/login/callback',
    postLogoutRedirectUri: POST_LOGOUT_REDIRECT_URI,
    // Keep using the environment's configured end-session endpoint (the same URL the old
    // shared_web_assets logout page called) instead of the SDK default {issuer}/v1/logout, so the
    // IdP-side logout behaviour is unchanged. Falls back to the SDK default when unconfigured.
    logoutUrl: ENDSESSION_ENDPOINT || undefined,
    // Standard OIDC scopes only. The 'default' Okta authorization server ships without custom scopes,
    // and APIM validates on audience + issuer (no scope claim required). Add a custom scope here only
    // if you later add one to the authorization server and require it in the APIM policy.
    scopes: ['openid', 'profile', 'email', 'offline_access'],
    responseType: ['code'],
    pkce: true,
    tokenManager: {
        storage: 'sessionStorage',
        autoRenew: true,
    },
    services: {
        autoRenew: true,
        syncStorage: true,
    },
});
